/* eslint-disable no-nested-ternary */
import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import DeleteIcon from "src/assets/images/settings/deleteIcon.png";
import GenericCardIcon from "src/assets/images/settings/default_generic_card.png";
import useStyle from "./Styles";

interface IPaymentCard {
  paymentId: string;
  brand: string;
  cvc: string; // Last 4 digits of the card
  expDate: string; // Expiry Date
  isDefault: boolean; // Is it default?
  isExpired: boolean; // Is it expired?
  onDelete: (paymentId: string, brand: string, expiry: string) => void; // Function to handle delete
  onSetDefault: () => void;
  //
  loading: boolean;
  toBeDefaultPaymentId: string;
}

const getCardLogo = (brand: string): string => {
  const normalized = brand?.toLowerCase().replace(/[\s_-]/g, "") ?? "";
  // ----------- Regional / Less Common (self-host or stable URLs) -----------

  if (normalized.includes("bccard")) {
    return "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/BC_CARD_CI.svg/250px-BC_CARD_CI.svg.png";
  }

  if (normalized.includes("dinacard")) {
    return "https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/%D0%94%D0%B8%D0%BD%D0%B0.png/250px-%D0%94%D0%B8%D0%BD%D0%B0.png";
  }

  if (normalized.includes("cartesbancaires")) {
    return "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/Logo_GIE_CB_%282024%29.svg/250px-Logo_GIE_CB_%282024%29.svg.png";
  }

  if (normalized.includes("eftpos")) {
    return "https://upload.wikimedia.org/wikipedia/commons/a/af/Eftpos_australia_new_logo.png";
  }

  // ----------- Global Networks (CDN - consistent & fast) -----------

  if (normalized.includes("visa")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/visa.svg";
  }

  if (normalized.includes("mastercard")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/mastercard.svg";
  }

  if (normalized.includes("amex") || normalized.includes("americanexpress")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/amex.svg";
  }

  if (normalized.includes("discover")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/discover.svg";
  }

  if (normalized.includes("diners")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/diners.svg";
  }

  if (normalized.includes("jcb")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/jcb.svg";
  }

  if (normalized.includes("unionpay")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/unionpay.svg";
  }

  if (normalized.includes("maestro")) {
    return "https://cdn.jsdelivr.net/npm/payment-icons/min/flat/maestro.svg";
  }

  // ----------- Fallback -----------

  return GenericCardIcon;
};

const PaymentCard: React.FC<IPaymentCard> = ({
  paymentId,
  brand,
  cvc,
  expDate,
  isDefault,
  isExpired,
  onDelete,
  onSetDefault,
  //
  loading,
  toBeDefaultPaymentId,
}) => {
  const { classes } = useStyle();
  return (
    <Box className={classes.paymentCardDetailsBox}>
      {/* Left Section */}
      <Box className={classes.paymentCardLeftDetails}>
        <img
          src={getCardLogo(brand)}
          alt={brand}
          width={60}
          className={classes.paymentCardImage}
        />
        <Box>
          <Typography
            sx={{ fontWeight: 600 }}
            className={classes.paymentCardDetail1}
          >
            {" "}
            {brand} ending in {cvc}
          </Typography>
          <Typography className={classes.paymentCardDetail2}>
            Exp. date {expDate}
          </Typography>
        </Box>
      </Box>

      {/* Right Section */}
      <Box className={classes.paymentCardRightControls}>
        {isDefault ? (
          <Chip label="Default" className={classes.defaultPaymentMethod} />
        ) : isExpired ? (
          <Chip label="Expired" className={classes.expiredPaymentMethod} />
        ) : (
          <Button
            variant="text"
            className={classes.setAsDefaultMehthod}
            onClick={onSetDefault}
            disabled={toBeDefaultPaymentId === paymentId && loading}
          >
            {/* Set as Default */}
            {toBeDefaultPaymentId === paymentId && loading
              ? "Setting as Default..."
              : "Set as Default"}
          </Button>
        )}

        {isDefault ? (
          <Tooltip
            placement="top-start"
            title="Before deleting the default one, any other payment method is required to be set as default"
          >
            <span style={{ cursor: "pointer" }}>
              {" "}
              <IconButton disabled>
                <img src={DeleteIcon} alt="delete icon" width={22} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton onClick={() => onDelete(paymentId, brand, expDate)}>
            <img src={DeleteIcon} alt="delete icon" width={22} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default PaymentCard;
